import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getWeChatOpenIdFromUrl } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';

interface SelectedFile {
    id: number;
    file: File;
}

interface UploadedAttachment {
    id?: number;
    fileName?: string;
    fileUrl?: string;
    fileType?: string;
    fileSize?: number;
    uploadedAt?: string;
}

const MaterialUploadPage: React.FC = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { weChatOpenId: authWeChatOpenId } = useAuth();
    const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadSuccessMessage, setUploadSuccessMessage] = useState<string | null>(null);
    const [uploadedAttachments, setUploadedAttachments] = useState<UploadedAttachment[]>([]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files ? Array.from(event.target.files) : [];
        if (!files.length) return;

        setUploadError(null);
        setUploadSuccessMessage(null);
        setUploadedAttachments([]);
        setSelectedFiles(prev => [
            ...prev,
            ...files.map(file => ({
                id: Date.now() + Math.random(),
                file
            }))
        ]);
    };

    const removeFile = (id: number) => {
        setSelectedFiles(prev => prev.filter(item => item.id !== id));
        setUploadError(null);
        setUploadSuccessMessage(null);
        setUploadedAttachments([]);
    };

    const handleUpload = async () => {
        if (!selectedFiles.length) return;

        try {
            setIsUploading(true);
            setUploadError(null);
            setUploadSuccessMessage(null);
            setUploadedAttachments([]);

            const formData = new FormData();
            const storedOpenId = localStorage.getItem('weChatOpenId') || localStorage.getItem('wechatOpenId');
            const urlOpenId = getWeChatOpenIdFromUrl();
            const wechatOpenId = storedOpenId || urlOpenId || authWeChatOpenId || '';
            if (!wechatOpenId) {
                throw new Error('缺少 wechatOpenId');
            }
            formData.append('wechatOpenId', wechatOpenId);
            selectedFiles.forEach(item => {
                formData.append('files', item.file);
            });

            const response = await fetch('/api/user-attachments/upload', {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || `上传失败: ${response.status}`);
            }

            const result = await response.json().catch(() => ({}));
            const success = result?.success !== undefined ? !!result.success : true;
            if (!success) {
                throw new Error(result?.message || '上传失败');
            }
            setSelectedFiles([]);
            setUploadedAttachments(Array.isArray(result?.attachments) ? result.attachments : []);
            setUploadSuccessMessage(result?.message || `已上传 ${result?.count || 0} 个文件`);

        } catch (error) {
            console.error('素材上传失败:', error);
            setUploadError(error instanceof Error ? error.message : '上传失败，请稍后再试');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-blue-100">
            <header className="sticky top-0 z-20 bg-white/80 backdrop-blur border-b border-slate-200">
                <div className="mx-auto flex items-center justify-between px-4 py-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600"
                    >
                        <span className="text-sm">←</span>
                        返回
                    </button>
                    <h1 className="text-base font-semibold text-slate-900">资料上传</h1>
                    <div className="w-10 h-7 rounded-full bg-gradient-to-r from-blue-500/70 to-indigo-500/70 blur-sm" />
                </div>
            </header>

            <main className="px-4 py-6 space-y-6">
                <section className="rounded-3xl bg-white shadow-lg shadow-slate-200/40 p-6 space-y-5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 text-xl text-white shadow-md shadow-blue-200">
                                ⬆
                            </div>
                            <div>
                                <p className="text-sm font-medium text-slate-800">本地文件</p>
                                <span className="text-xs text-slate-400">支持多选</span>
                            </div>
                        </div>
                        <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-500">
                            {selectedFiles.length} 个文件
                        </span>
                    </div>

                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3 text-base font-medium text-white shadow-lg shadow-blue-200 active:brightness-95"
                    >
                        选择文件
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleFileChange}
                    />

                    {selectedFiles.length > 0 && (
                        <div className="space-y-3">
                            <div className="space-y-2">
                                {selectedFiles.map(item => (
                                    <div
                                        key={item.id}
                                        className="flex items-center justify-between rounded-xl bg-slate-100/70 px-3 py-2 text-sm text-slate-700"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="text-base">📄</span>
                                            <span className="truncate max-w-[200px]">{item.file.name}</span>
                                        </div>
                                        <button
                                            onClick={() => removeFile(item.id)}
                                            className="text-xs text-slate-400"
                                        >
                                            删除
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={handleUpload}
                                disabled={isUploading}
                                className="w-full rounded-2xl bg-slate-900 py-3 text-base font-medium text-white shadow-lg shadow-slate-300/50 active:translate-y-[1px] disabled:bg-slate-400"
                            >
                                {isUploading ? '上传中…' : '开始上传'}
                            </button>

                            {uploadError && (
                                <div className="rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-500">
                                    {uploadError}
                                </div>
                            )}

                            {uploadSuccessMessage && (
                                <div className="rounded-2xl bg-emerald-50 px-4 py-3 text-xs text-emerald-600">
                                    {uploadSuccessMessage}
                                </div>
                            )}
                        </div>
                    )}
                </section>

                {uploadSuccessMessage && (
                    <section className="rounded-3xl bg-white shadow-md shadow-emerald-100/60 p-5 space-y-3">
                        <div className="flex items-start gap-3">
                            <div className="mt-1 text-lg">✅</div>
                            <div className="space-y-2">
                                <div className="text-sm font-medium text-emerald-600">上传成功</div>
                                <div className="text-xs text-emerald-500">{uploadSuccessMessage}</div>
                            </div>
                        </div>

                        {uploadedAttachments.length > 0 && (
                            <ul className="space-y-2">
                                {uploadedAttachments.map((item, index) => (
                                    <li
                                        key={item.id || index}
                                        className="rounded-2xl border border-emerald-100 bg-emerald-50/60 px-4 py-3 text-xs text-emerald-700"
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="font-medium truncate">{item.fileName || '未命名文件'}</span>
                                            {item.fileUrl && (
                                                <a
                                                    href={item.fileUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-emerald-500"
                                                >
                                                    查看
                                                </a>
                                            )}
                                        </div>
                                        <div className="mt-1 text-emerald-500/80">
                                            {item.fileType || '未知类型'} · {(item.fileSize ? (item.fileSize / 1024).toFixed(1) : '?')} KB
                                        </div>
                                        {item.uploadedAt && (
                                            <div className="text-emerald-500/70">
                                                上传时间：{new Date(item.uploadedAt).toLocaleString()}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                )}

                {uploadError && selectedFiles.length === 0 && (
                    <section className="rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-500">
                        {uploadError}
                    </section>
                )}
            </main>
        </div>
    );
};

export default MaterialUploadPage;
